import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, Home, Clock, Award, Shield, ChevronRight, Star, MapPin, TestTube, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { getLabTests, getTestPackages } from "@/services/data";
import { useCart } from "@/contexts/CartContext";
import { useState } from "react";
import { toast } from "sonner";
import TestCard from "@/components/TestCard";
import PackageCard from "@/components/PackageCard";

const stats = [
  { label: "Tests Available", value: "500+", icon: TestTube },
  { label: "Cities", value: "50+", icon: MapPin },
  { label: "Patients Served", value: "2M+", icon: Star },
  { label: "NABL Accredited", value: "Labs", icon: Award },
];

const categories = [
  { label: "Blood Tests", icon: "🩸", href: "/tests?category=Blood Test" },
  { label: "Thyroid", icon: "🦋", href: "/tests?category=Thyroid" },
  { label: "Diabetes", icon: "📊", href: "/tests?category=Diabetes" },
  { label: "Heart Health", icon: "❤️", href: "/tests?category=Heart Health" },
  { label: "Vitamins", icon: "💊", href: "/tests?category=Vitamins" },
  { label: "Liver", icon: "🫀", href: "/tests?category=Liver" },
  { label: "Kidney", icon: "🫘", href: "/tests?category=Kidney" },
  { label: "Cancer Markers", icon: "🔬", href: "/tests?category=Cancer Marker" },
];

export default function HomePage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const { data: tests } = useQuery({ queryKey: ["tests"], queryFn: getLabTests });
  const { data: packages } = useQuery({ queryKey: ["packages"], queryFn: getTestPackages });
  const { addTest, addPackage, hasItem } = useCart();

  const popularTests = tests?.filter(t => t.popular).slice(0, 4) || [];
  const popularPackages = packages?.filter(p => p.popular).slice(0, 3) || [];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (search.trim()) navigate(`/tests?search=${encodeURIComponent(search)}`);
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary/5 via-background to-primary/10 py-16 md:py-24">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-primary/10 text-primary hover:bg-primary/15 border-0">
                🏆 NABL Accredited Labs · ISO 15189:2022
              </Badge>
              <h1 className="text-4xl md:text-5xl font-bold text-foreground leading-tight mb-4">
                Book Lab Tests &<br />
                <span className="text-primary">Health Checkups</span><br />
                from Home
              </h1>
              <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
                Accurate results, certified labs, and free home sample collection. Get reports in 4–6 hours across 50+ cities.
              </p>

              {/* Search */}
              <form onSubmit={handleSearch} className="flex gap-2 max-w-lg mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search for tests, packages..."
                    className="pl-9 h-12 text-sm"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                  />
                </div>
                <Button type="submit" className="h-12 px-6">Search</Button>
              </form>

              <div className="flex flex-wrap gap-2">
                {["CBC", "Thyroid", "Diabetes", "Vitamin D", "Lipid Profile"].map(tag => (
                  <button
                    key={tag}
                    onClick={() => navigate(`/tests?search=${tag}`)}
                    className="text-xs px-3 py-1.5 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors font-medium"
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Hero image / visual */}
            <div className="hidden lg:block relative">
              <img
                src="https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=600&h=500&fit=crop"
                alt="Lab technician"
                className="rounded-2xl shadow-2xl w-full h-[420px] object-cover"
              />
              <div className="absolute -bottom-4 -left-4 bg-background rounded-xl shadow-lg p-4 flex items-center gap-3 border border-border">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Report Ready In</p>
                  <p className="font-bold text-sm text-foreground">4–6 Hours</p>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 bg-background rounded-xl shadow-lg p-4 flex items-center gap-3 border border-border">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Home className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Free Home Collection</p>
                  <p className="font-bold text-sm text-foreground">Above ₹500</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-border bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map(stat => (
              <div key={stat.label} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <stat.icon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-xl font-bold text-foreground">{stat.value}</p>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Browse by Category</h2>
            <p className="text-muted-foreground text-sm mt-1">Find the right test for your health needs</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/tests")} className="text-primary">
            View All <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
        <div className="grid grid-cols-4 sm:grid-cols-8 gap-3">
          {categories.map(cat => (
            <button
              key={cat.label}
              onClick={() => navigate(cat.href)}
              className="flex flex-col items-center gap-2 p-3 rounded-xl bg-card border border-border hover:border-primary/50 hover:bg-primary/5 transition-all group"
            >
              <span className="text-2xl">{cat.icon}</span>
              <span className="text-xs font-medium text-foreground text-center leading-tight group-hover:text-primary transition-colors">{cat.label}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Popular Tests */}
      <section className="bg-muted/30 py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-foreground">Popular Lab Tests</h2>
              <p className="text-muted-foreground text-sm mt-1">Most booked tests by our patients</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate("/tests")} className="text-primary">
              View All <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {popularTests.map(test => (
              <TestCard key={test.id} test={test} />
            ))}
          </div>
        </div>
      </section>

      {/* Health Packages */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Health Checkup Packages</h2>
            <p className="text-muted-foreground text-sm mt-1">Comprehensive panels for complete wellness</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/packages")} className="text-primary">
            View All <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {popularPackages.map(pkg => (
            <PackageCard key={pkg.id} pkg={pkg} />
          ))}
        </div>
      </section>

      {/* Home Collection Banner */}
      <section className="bg-gradient-to-r from-primary to-primary/80 py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="text-primary-foreground">
              <h2 className="text-3xl font-bold mb-4">Free Home Sample Collection</h2>
              <p className="text-primary-foreground/80 text-lg mb-6 leading-relaxed">
                Skip the clinic visit. Our trained phlebotomists come to your doorstep at your preferred time. Safe, hygienic, and convenient.
              </p>
              <ul className="space-y-2 mb-8">
                {["Certified & trained phlebotomists", "Available 7 days a week (7AM–9PM)", "Real-time tracking of your sample", "Digital reports on WhatsApp & Email"].map(item => (
                  <li key={item} className="flex items-center gap-2 text-primary-foreground/90 text-sm">
                    <div className="w-5 h-5 rounded-full bg-primary-foreground/20 flex items-center justify-center shrink-0">
                      <span className="text-xs">✓</span>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              <Button
                variant="secondary"
                size="lg"
                onClick={() => navigate("/home-collection")}
                className="bg-background text-foreground hover:bg-background/90"
              >
                Book Home Collection
              </Button>
            </div>
            <div className="hidden lg:block">
              <img
                src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=500&h=350&fit=crop"
                alt="Home collection"
                className="rounded-2xl shadow-xl w-full h-[300px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Trust indicators */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <h2 className="text-2xl font-bold text-center text-foreground mb-10">Why Choose DiagnoPlus?</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: Award, title: "NABL Accredited", desc: "ISO 15189:2022 certified labs" },
            { icon: Shield, title: "Accurate Results", desc: "99.9% accuracy with quality controls" },
            { icon: Clock, title: "Fast Reports", desc: "Results in 4–24 hours" },
            { icon: Home, title: "Home Collection", desc: "Free pickup across 50+ cities" },
          ].map(item => (
            <div key={item.title} className="flex flex-col items-center text-center p-6 rounded-xl bg-card border border-border hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                <item.icon className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold text-foreground mb-1">{item.title}</h3>
              <p className="text-xs text-muted-foreground">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
